import { useCallback, useState } from 'react';
import { httpGet } from '../../components/utils';

export interface FileGenerationResponse {
    responseIds: string | string[];
}

export interface UseFileGenerationParams<TResponse extends FileGenerationResponse = FileGenerationResponse> {
    generateUrl: string;
    queryParams?: Record<string, string>;
    onFailure?: (error: unknown) => void;
}

export interface UseFileGenerationReturn<TResponse extends FileGenerationResponse = FileGenerationResponse> {
    generate: () => Promise<TResponse['responseIds'] | undefined>;
    loading: boolean;
    error: unknown;
}

export const useFileGeneration = <TResponse extends FileGenerationResponse = FileGenerationResponse>({
    generateUrl,
    queryParams = {},
    onFailure
}: UseFileGenerationParams<TResponse>): UseFileGenerationReturn<TResponse> => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<unknown | undefined>();

    const generate = useCallback(async () => {
        setLoading(true);
        setError(undefined);

        try {
            const fullUrl = `${generateUrl}?${new URLSearchParams(queryParams).toString()}`;
            const response = await httpGet(fullUrl);
            const { responseIds } = response.data as unknown as TResponse;
            return responseIds;
        } catch (err) {
            setError(err);
            onFailure?.(err);
        } finally {
            setLoading(false);
        }
    }, [generateUrl, queryParams, onFailure]);

    return { generate, loading, error };
};
